import type { ReferenceElement } from "@floating-ui/dom";
import { flip, offset, shift } from "@floating-ui/dom";
import { FC, useEffect, useRef, useState } from "react";
import { ITooltip, Tooltip, TooltipRefProps } from "react-tooltip";
import { twMerge } from "tailwind-merge";

type CustomTooltipProps = {} & ITooltip;

const DEFAULT_TOOLTIP_OFFSET = 6;

const getAnchorOffset = (anchor?: ReferenceElement | null) => {
  if (!anchor || typeof Element === "undefined") return DEFAULT_TOOLTIP_OFFSET;
  if (!(anchor instanceof Element)) return DEFAULT_TOOLTIP_OFFSET;
  const rawOffset = anchor.getAttribute("data-tooltip-offset");
  const parsedOffset = rawOffset ? Number.parseFloat(rawOffset) : Number.NaN;
  return Number.isFinite(parsedOffset) ? parsedOffset : DEFAULT_TOOLTIP_OFFSET;
};

const escapeAttrValue = (value: string) =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(/'/g, "\\'");

const CustomTooltip: FC<CustomTooltipProps> = ({
  className,
  place,
  positionStrategy,
  middlewares,
  hidden,
  ...rest
}) => {
  const tooltipRef = useRef<TooltipRefProps>(null);
  const suppressedAnchorRef = useRef<Element | null>(null);
  const [suppressed, setSuppressed] = useState(false);

  const { id, anchorSelect } = rest;
  const managesOwnVisibility = Boolean(
    rest.openOnClick || rest.openEvents?.click || rest.imperativeModeOnly,
  );

  // react-tooltip keeps its `delayShow` timer running when the anchor is
  // clicked before the tooltip has appeared, so a quick click still pops the
  // tooltip open right after the action fires. There's no public API to cancel
  // that pending timer, so we gate the tooltip with `hidden` from the moment
  // the anchor is pressed until the pointer/focus leaves it again.
  useEffect(() => {
    // Information icons and explicitly controlled tooltips manage clicks
    // themselves; only action tooltips suppress the delayed hover tooltip.
    if (managesOwnVisibility) return undefined;
    if (typeof document === "undefined") return undefined;

    const selectors: string[] = [];
    if (id) selectors.push(`[data-tooltip-id='${escapeAttrValue(id)}']`);
    if (anchorSelect) selectors.push(anchorSelect);
    if (selectors.length === 0) return undefined;
    const selector = selectors.join(",");

    const resolveAnchor = (target: EventTarget | null): Element | null => {
      if (!(target instanceof Element)) return null;
      try {
        return target.closest(selector);
      } catch {
        return null;
      }
    };

    const release = () => {
      if (!suppressedAnchorRef.current) return;
      suppressedAnchorRef.current = null;
      // Make sure any swallowed "show" is driven back to closed before we stop
      // gating, so the next hover starts from a clean state.
      tooltipRef.current?.close();
      setSuppressed(false);
    };

    const handlePointerDown = (event: PointerEvent) => {
      const anchor = resolveAnchor(event.target);
      if (!anchor) return;
      // Already visible: let react-tooltip's own click close-event handle it.
      if (tooltipRef.current?.isOpen) return;
      suppressedAnchorRef.current = anchor;
      setSuppressed(true);
    };

    // Allow tooltips again as soon as the pointer moves off the pressed anchor.
    const handlePointerOver = (event: PointerEvent) => {
      const suppressedAnchor = suppressedAnchorRef.current;
      if (!suppressedAnchor) return;
      const anchor = resolveAnchor(event.target);
      if (anchor === suppressedAnchor && suppressedAnchor.isConnected) return;
      release();
    };

    const handleFocusOut = (event: FocusEvent) => {
      const suppressedAnchor = suppressedAnchorRef.current;
      if (!suppressedAnchor) return;
      const target = event.target;
      if (
        target === suppressedAnchor ||
        (target instanceof Node && suppressedAnchor.contains(target))
      ) {
        release();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointerover", handlePointerOver, true);
    document.addEventListener("focusout", handleFocusOut, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointerover", handlePointerOver, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      suppressedAnchorRef.current = null;
    };
  }, [id, anchorSelect, managesOwnVisibility]);

  // No explicit boundary: with the `fixed` position strategy the default
  // clipping boundary is the viewport. Using `document.body` here breaks once
  // the page is scrolled (body extends above the viewport), letting the
  // tooltip flip/shift off-screen.
  const resolvedMiddlewares = middlewares ?? [
    offset(({ elements }) => getAnchorOffset(elements.reference)),
    shift({ padding: 8 }),
    flip({ padding: 8 }),
  ];

  return (
    <Tooltip
      ref={tooltipRef}
      opacity={1}
      delayShow={200}
      delayHide={0}
      closeEvents={{
        click: true,
        mouseout: true,
        blur: true,
      }}
      {...rest}
      hidden={hidden || suppressed}
      place={place ?? "top"}
      positionStrategy={positionStrategy ?? "fixed"}
      middlewares={resolvedMiddlewares}
      classNameArrow="invisible"
      // Merged, so a caller's padding or width replaces the base's (both
      // are !important; Tailwind's own order would let the base win).
      className={twMerge(
        "!rounded-lg !bg-tooltip !transition-none !z-[2147483647] shadow-2xl !shadow-slate-950/20 border border-white/10 !text-tooltip-foreground dark !text-xs !font-normal !font-sans !leading-snug !py-1.5 !px-2.5 max-w-[240px]",
        className,
      )}
      id={rest.id}
    />
  );
};

export default CustomTooltip;
